<script>
import BaseModal from 'dashboard/components/dahreply/BaseModal.vue';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'CreateBranchModal',
  components: { BaseModal },
  props: {
    branch: {
      type: Object,
      default: null,
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['close', 'confirm'],
  data() {
    return {
      name: '',
      tel: '',
      address: '',
      isLoading: false,
      errors: {
        name: '',
        tel: '',
        address: '',
      },
    };
  },
  computed: {
    isEditMode() {
      return this.isEdit || !!this.branch;
    },
    modalTitle() {
      return this.isEditMode ? 'Edit Branch' : 'Create Branch';
    },
    confirmButtonText() {
      return this.isEditMode ? 'Update' : 'Create';
    },
  },
  watch: {
    branch: {
      handler(newBranch) {
        if (newBranch) this.populateForm(newBranch);
      },
      immediate: true,
    },
  },
  methods: {
    populateForm(branchData) {
      this.name = branchData.name || branchData.branch_name || '';
      this.tel = branchData.phone_number || '';
      this.address = branchData.address || '';
    },
    validateForm() {
      this.clearErrors();
      let isValid = true;

      if (!this.name.trim()) {
        this.errors.name = 'Branch name is required';
        isValid = false;
      }
      if (!this.tel.trim()) {
        this.errors.tel = 'Phone number is required';
        isValid = false;
      } else if (!this.isValidPhoneNumber(this.tel)) {
        this.errors.tel = 'Please enter a valid phone number';
        isValid = false;
      }
      if (!this.address.trim()) {
        this.errors.address = 'Address is required';
        isValid = false;
      }

      return isValid;
    },
    isValidPhoneNumber(phone) {
      // const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
      // return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
      return true;
    },
    clearErrors() {
      this.errors = { name: '', tel: '', address: '' };
    },
    async handleConfirm() {
      if (!this.validateForm()) return;
      this.isLoading = true;

      try {
        const branchData = {
          name: this.name.trim(),
          phone_number: this.tel.trim(),
          address: this.address.trim(),
        };
        if(!this.isEditMode){
          await this.$store.dispatch('branchV2/createBranch', branchData);
          useAlert('Branch created');
          this.$store.dispatch('onboarding/completeLocalTask', 'create_branch');
          if(this.$route.query.onboarding === 'true') {
            this.$router.push(this.$route.path);
          }
        } else {
          await this.$store.dispatch('branchV2/updateBranch', {
            branchData: branchData, branchId: this.branch.id || this.branch.branch_id
          });
          useAlert('Branch updated')
        }
        this.$emit('refresh');
        this.$emit('close');
      } catch (error) {
        console.error('Error handling branch:', error);
      } finally {
        this.isLoading = false;
      }
    },
    resetForm() {
      this.name = '';
      this.tel = '';
      this.address = '';
      this.clearErrors();
    },
  },
  mounted() {
    if (!this.isEditMode) this.resetForm();
  },
};
</script>

<template>
  <BaseModal @close="$emit('close')">
    <template #title>
      {{ modalTitle }}
    </template>

    <form @submit.prevent="handleConfirm" class="space-y-6">
      <!-- Branch Name -->
      <div>
        <label for="branch-name" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Branch Name
        </label>
        <input
          id="branch-name"
          type="text"
          v-model="name"
          placeholder="Enter branch name"
          :class="[
            'reset-base w-full rounded-md border p-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-woot-500',
            errors.name ? 'border-red-500 ring-red-500' : 'border-slate-300 dark:border-slate-700'
          ]"
        />
        <p v-if="errors.name" class="text-xs text-red-500 mt-1">{{ errors.name }}</p>
      </div>

      <!-- Phone Number -->
      <div>
        <label for="branch-phone" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Phone Number
        </label>
        <input
          id="branch-phone"
          type="tel"
          v-model="tel"
          placeholder="Enter phone number"
          :class="[
            'reset-base w-full rounded-md border p-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-woot-500',
            errors.tel ? 'border-red-500 ring-red-500' : 'border-slate-300 dark:border-slate-700'
          ]"
        />
        <p v-if="errors.tel" class="text-xs text-red-500 mt-1">{{ errors.tel }}</p>
      </div>

      <!-- Address -->
      <div>
        <label for="branch-address" class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
          Address
        </label>
        <textarea
          id="branch-address"
          v-model="address"
          rows="3"
          placeholder="Enter branch address"
          :class="[
            'reset-base w-full rounded-md border p-2.5 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-woot-500 resize-y min-h-[80px]',
            errors.address ? 'border-red-500 ring-red-500' : 'border-slate-300 dark:border-slate-700'
          ]"
        ></textarea>
        <p v-if="errors.address" class="text-xs text-red-500 mt-1">{{ errors.address }}</p>
      </div>
    </form>

    <template #footer>
      <div class="flex justify-end gap-3 mt-4">
        <woot-button color-scheme="secondary" @click="$emit('close')" :disabled="isLoading">
          Cancel
        </woot-button>
        <woot-button @click="handleConfirm" :disabled="isLoading" :loading="isLoading">
          {{ confirmButtonText }}
        </woot-button>
      </div>
    </template>
  </BaseModal>
</template>
